import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative flex-col justify-between bg-slate-900 text-white p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://static.prod-images.emergentagent.com/jobs/5ce631ed-49ec-4d9b-a9a4-33d86441716b/images/b0359cb523d8971a22a3c4cfbc723e4cf07dcd8acae81cc9a5384d47d2d30b7f.png" alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/80 to-slate-900"/>
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 text-sm hover:text-white" data-testid="login-back-home">
            <ArrowLeft className="h-4 w-4"/> Back to website
          </Link>
        </div>
        <div className="relative max-w-md">
          <div className="eyebrow text-[#C2A671]">VCAJ Portal</div>
          <h1 className="font-serif text-5xl mt-4 leading-[1.1]">Adding value.<br/>Building trust.<br/><span className="text-[#C2A671]">Leading excellence.</span></h1>
          <p className="mt-6 text-slate-300">Sign in to manage clients, projects, timesheets and reimbursements — all in one place.</p>
        </div>
        <div className="relative text-xs uppercase tracking-[0.25em] text-slate-500">© {new Date().getFullYear()} VCAJ & Associates LLP</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden text-sm text-slate-500 mb-6 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4"/> Home</Link>
          <div className="eyebrow">Sign in</div>
          <h2 className="font-serif text-4xl mt-3">Welcome back.</h2>
          <p className="text-slate-600 mt-2 text-sm">Access the VCAJ internal portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="eyebrow">Email</Label>
              <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="mt-2 rounded-sm" data-testid="login-email"/>
            </div>
            <div>
              <Label className="eyebrow">Password</Label>
              <Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="mt-2 rounded-sm" data-testid="login-password"/>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm h-11" data-testid="login-submit">{busy ? "Signing in..." : "Sign in"}</Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-slate-400">
            <div className="h-px flex-1 bg-slate-200"/> or <div className="h-px flex-1 bg-slate-200"/>
          </div>

          <Button onClick={googleSignIn} variant="outline" className="w-full rounded-sm h-11 border-slate-300" data-testid="login-google">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M23 12.27c0-.81-.07-1.6-.21-2.36H12v4.46h6.18a5.28 5.28 0 0 1-2.29 3.46v2.87h3.7c2.17-2 3.41-4.95 3.41-8.43z"/><path fill="#34A853" d="M12 23c3.09 0 5.69-1.02 7.59-2.78l-3.7-2.87a6.93 6.93 0 0 1-10.46-3.66H1.61v2.96A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.43 13.69a6.6 6.6 0 0 1 0-3.38V7.35H1.61a11.02 11.02 0 0 0 0 9.3l3.82-2.96z"/><path fill="#EA4335" d="M12 5.38c1.68 0 3.19.58 4.38 1.72l3.28-3.28C17.69 1.99 15.09 1 12 1A11 11 0 0 0 1.61 7.35l3.82 2.96A6.59 6.59 0 0 1 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <p className="text-sm text-slate-600 mt-8 text-center">
            New here? <Link to="/register" className="text-slate-900 underline underline-offset-4" data-testid="login-to-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
